import {
  Input,
  TextInput as MantineTextInput,
  TextInputProps as MantineTextInputProps,
  rem,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import clsx from 'clsx';
import { CSSProperties, ReactNode, forwardRef } from 'react';

import { Link } from '../Link';
import { ReadOnlyLock } from '../ReadOnlyLock';
import styles from './TextInput.module.css';

export type TextInputSize = 'md' | 'lg';

export interface TextInputProps extends Omit<
  MantineTextInputProps,
  'variant' | 'color' | 'styles' | 'classNames' | 'style' | 'size' | 'readOnly'
> {
  /** Input size: 'md' (40px) or 'lg' (48px). Default: 'md' */
  size?: TextInputSize;
  /** Add 'fs-mask' class for FullStory masking of sensitive data */
  isMasked?: boolean;
  isReadOnly?: boolean;
  /** Readonly visual style. 'label' (default) hides border/sections and renders as plain text; 'lock' keeps the border and shows a lock icon on the right; 'url' renders the value as a link that opens in a new tab. Only applies when `isReadOnly` is true. */
  readOnlyVariant?: 'label' | 'lock' | 'url';
  /** Visual variant. 'info' renders an info-circle icon in the input's right section. Default: 'default' */
  variant?: 'default' | 'info';
}

/** Wrap a description/info message with a leading info-circle icon. String
 * descriptions get the icon; non-string ReactNodes pass through untouched;
 * empty → undefined. */
const renderDescription = (
  description: TextInputProps['description']
): ReactNode => {
  if (!description) return undefined;
  if (typeof description !== 'string') return description;

  return (
    <span className={styles.descriptionWrapper}>
      <IconInfoCircle size={12} stroke={2} />
      <span>{description}</span>
    </span>
  );
};

/** Readonly 'url' variant render: shows the value as a link that opens in a new
 * tab. Reuses Input.Wrapper so label/required/description styling stays
 * consistent with the rest of the component. */
const ReadOnlyUrlField = ({
  label,
  value,
  required,
  description,
}: Pick<TextInputProps, 'label' | 'required' | 'description' | 'value'>) => {
  const url = typeof value === 'string' ? value : '';

  return (
    <Input.Wrapper
      unstyled
      classNames={{
        root: styles.root,
        label: styles.label,
        required: styles.required,
        description: styles.description,
      }}
      label={label}
      required={required}
      description={renderDescription(description)}
      inputWrapperOrder={['label', 'input', 'description', 'error']}
    >
      {url ? (
        <Link href={url} target="_blank" variant="primary">
          {url}
        </Link>
      ) : (
        <span className={styles.urlEmpty}>—</span>
      )}
    </Input.Wrapper>
  );
};

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      size = 'md',
      label,
      error,
      disabled,
      isMasked = false,
      rightSectionWidth,
      leftSectionWidth,
      isReadOnly,
      readOnlyVariant = 'label',
      variant = 'default',
      description,
      leftSection,
      rightSection,
      ...props
    },
    ref
  ) => {
    const isInfoVariant = variant === 'info';
    const renderError = (): ReactNode => {
      // Suppress error rendering entirely in read-only mode
      if (isReadOnly) return undefined;
      return error || undefined;
    };

    // Mantine's `unstyled` mode strips its built-in CSS, including the logic
    // that sizes sections and adjusts input padding based on rightSectionWidth /
    // leftSectionWidth. We bridge the gap by setting CSS custom properties on the
    // wrapper, which our TextInput.module.css reads to set both the section width
    // and the matching input padding (falling back to icon-sized defaults).
    const sectionWidthVars: Record<string, string> = {};
    if (rightSectionWidth !== undefined && rightSectionWidth !== null) {
      sectionWidthVars['--right-section-width'] = rem(rightSectionWidth);
    }
    if (leftSectionWidth !== undefined && leftSectionWidth !== null) {
      sectionWidthVars['--left-section-width'] = rem(leftSectionWidth);
    }

    const activeReadOnlyVariant = isReadOnly ? readOnlyVariant : undefined;
    const isLockVariant = activeReadOnlyVariant === 'lock';

    // Right section resolution: lock icon > (read-only hides it) > info icon >
    // caller-provided section.
    const resolveRightSection = (): ReactNode => {
      if (isLockVariant) return <ReadOnlyLock />;
      if (isReadOnly) return undefined;
      if (isInfoVariant) return <IconInfoCircle size={16} stroke={2} />;
      return rightSection;
    };

    if (activeReadOnlyVariant === 'url') {
      return (
        <ReadOnlyUrlField
          label={label}
          value={props.value}
          required={props.required}
          description={description}
        />
      );
    }

    return (
      <MantineTextInput
        ref={ref}
        unstyled
        classNames={{
          root: styles.root,
          wrapper: styles.wrapper,
          input: clsx(styles.input, isMasked && 'fs-mask'),
          section: styles.section,
          label: styles.label,
          error: styles.error,
          required: styles.required,
          description: styles.description,
        }}
        wrapperProps={{
          'data-input-size': size,
          style: sectionWidthVars as CSSProperties,
        }}
        rightSectionWidth={rightSectionWidth}
        leftSectionWidth={leftSectionWidth}
        label={label}
        description={renderDescription(description)}
        inputWrapperOrder={['label', 'input', 'description', 'error']}
        error={renderError()}
        disabled={disabled}
        readOnly={isReadOnly}
        data-readonly-variant={isLockVariant ? 'lock' : undefined}
        leftSection={isReadOnly && !isLockVariant ? undefined : leftSection}
        rightSection={resolveRightSection()}
        tabIndex={isReadOnly ? -1 : undefined}
        {...props}
      />
    );
  }
);

TextInput.displayName = 'TextInput';
